import { Card, CardContent, Typography, Box } from "@mui/material";
import GenericTable from "../../Components/Back-End/Table";
import { useState } from "react";
import ReactQuill from "react-quill";
import 'react-quill/dist/quill.snow.css';
import SectionCard from "../../Components/Back-End/SectionCard";



export function EventsTable() {
    const [validationErrors, setValidationErrors] = useState({});

    const columns = [
        {
            accessorKey: "id",
            header: "ID",
            enableEditing: false,
        },
        {
            accessorKey: "eventName",
            header: "Event Name",
            validate: (value) => {
                if (!value || value.trim() === "") return "Event name is required.";
            },
            muiEditTextFieldProps: () => ({
                required: true,
                error: !!validationErrors.eventName,
                helperText: validationErrors.eventName ?? '',
                onChange: () => {
                    setValidationErrors(prev => ({
                        ...validationErrors,
                        eventName: undefined
                    }));
                }
            }),
        },
        {
            accessorKey: "location",
            header: "Location",
            validate: (value) => {
                if (!value || value.trim() === "") return "Location is required.";
            },
            muiEditTextFieldProps: () => ({
                required: true,
                error: !!validationErrors.location,
                helperText: validationErrors.location ?? '',
                onChange: () => {
                    setValidationErrors(prev => ({
                        ...validationErrors,
                        location: undefined
                    }));
                }
            }),
        },
        {
            accessorKey: "startDate",
            header: "Start Date",
            Cell: ({ cell }) =>
                cell.getValue() ? new Date(cell.getValue()).toLocaleString() : '',
            muiEditTextFieldProps: ({ row }) => ({
                required: true,
                type: "datetime-local",
                InputLabelProps: { shrink: true },
                error: !!validationErrors.startDate,
                helperText: validationErrors.startDate ?? '',
                onChange: () => {
                    setValidationErrors(prev => ({
                        ...validationErrors,
                        startDate: undefined
                    }));
                }
            }),
            validate: (value) => {
                if (!value) return "Start date is required.";
            },
        },
        {
            accessorKey: "endTime",
            header: "End Time",
            enableEditing: true,
            Cell: ({ cell }) => {
                const value = cell.getValue();
                if (!value) return '';

                const [hourStr, minuteStr] = value.split(':');
                const hour = parseInt(hourStr, 10);
                const minute = parseInt(minuteStr, 10);

                const date = new Date();
                date.setHours(hour);
                date.setMinutes(minute);

                return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            },
            muiEditTextFieldProps: () => ({
                required: true,
                type: "time",
                InputLabelProps: { shrink: true },
                error: !!validationErrors.endTime,
                helperText: validationErrors.endTime ?? '',
                onChange: () => {
                    setValidationErrors(prev => ({
                        ...prev,
                        endTime: undefined
                    }));
                }
            }),
            validate: (value, row) => {
                if (!value) return "End time is required.";

                const startDate = row?.startDate;
                //console.log(startDate)
                if (!startDate) return;

                const [endHour, endMinute] = value.split(':').map(Number);

                const start = new Date(startDate);
                const end = new Date(startDate); 
                end.setHours(endHour);
                end.setMinutes(endMinute);
                
                //console.log(end)
                //console.log(start)

                if (end < start) {
                    return "End time cannot be earlier than start time.";
                }
            }
        },
        {
            header: "URL",
            accessorKey: "googleMapsUrl",
            meta: { hideInTable: true },
            validate: (value) => {
                if (!value) return; // optional field
                const pattern = /^https:\/\/www\.google\.com\/maps\/embed\?pb=.+$/;
                if (!pattern.test(value.trim())) {
                    return "Please enter a valid Google Maps embed URL.";
                }
            },
            muiEditTextFieldProps: () => ({
                placeholder: "Paste Google Maps embed URL",
                error: !!validationErrors.googleMapsUrl,
                helperText: validationErrors.googleMapsUrl ?? '',
                onChange: (e) => {
                    const value = e.target.value;
                    setValidationErrors(prev => ({
                        ...prev,
                        googleMapsUrl: undefined
                    }));
                }
            }),
        },
        {
            accessorKey: "eventDescription",
            meta: { hideInTable: true },
            header: "Event Description",
            Edit: ({ cell, column, row, table }) => {
                const value = cell.getValue() || "";

                const handleChange = (content, delta, source, editor) => {
                    row._valuesCache[column.id] = editor.getHTML();
                    table.setEditingRow(row); // or however your table API triggers a state update
                };

                return (
                    <Box
                        sx={{
                            '& .ql-tooltip': {
                                position: 'absolute',
                                left: '50% !important', // center horizontally
                                transform: 'translateX(-50%)', // shift it back to actual center
                                overflow: 'visible',
                            },
                            '& .ql-container': {
                                overflow: 'visible',
                            },
                        }}
                    >
                        <Typography
                            sx={{
                                fontWeight: 300,
                                marginTop: '0.5rem',
                                fontSize: '0.8rem',
                            }}
                        >
                            Event Description
                        </Typography>

                        <ReactQuill
                            theme="snow"
                            value={value}
                            onChange={handleChange}
                            modules={{
                                toolbar: [
                                    ['bold', 'underline'],
                                    [{ color: [] }, { background: [] }],
                                    ['link'],
                                ],
                            }}
                            style={{
                                height: 250,
                                marginBottom: '4.5rem',
                            }}
                        />
                    </Box>
                );

            },
            Cell: ({ cell }) => (
                <div dangerouslySetInnerHTML={{ __html: cell.getValue() }} />
            ),
        },
    ];

    const columnVisibility = columns.reduce((acc, col) => {
        if (col.meta?.hideInTable) {
            acc[col.accessorKey] = false;
        }
        return acc;
    }, {});
    
    return (
        <SectionCard title="Events" subtitle="Manage your events below">
            <GenericTable
                columns={columns}
                itemName="Event"
                createApi="/api/events/save"
                readApi="/api/events/GetEvents"
                updateApi="/api/events/save"
                deleteApi="/api/events/delete"
                columnVisibility={columnVisibility}
                validationErrors={validationErrors}
                setValidationErrors={setValidationErrors}
                publicSitePage="Events"
            />
        </SectionCard>
    );
}
